import * as React from "react";
import { useEffect } from "react";
import { QrReader } from "react-qr-reader";
import { useNavigate } from "react-router-dom";
import { setCurrentStep } from "./Stepper/stepperSlice";
import { useDispatch } from "react-redux";
import NavbarStepper from "./Stepper/NavBarStepper";
import { useParams } from "react-router-dom";
import { useEditSmartNodeMutation, useGetIMSSmartnodeQuery } from "../api/apiSlice";
import { showError } from "../../Global/notifications/notificationsSlice";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useConnectionMonitor } from "./connectionsTest";
import ConnectionAlert from "./ConnectionAlert";
import LoadingOverlay from "./LoadingOverlay";
import { useStepperStateRestoration } from "./useStepperStateRestoration";
import { Box, Typography } from "@mui/material";
import AbortJobButton from "./AbortJobButton";
import { parseDeviceQr } from "./parseDeviceQr";
import { useRefusedScan } from "./useRefusedScan";

function QRCodeScanPage() {
  useStepperStateRestoration();

  const {
    isConnected,
    showAlert,
    showWeakWarning,
    handleRetry,
    handleDismiss,
    handleWeakWarningDismiss,
    responseTime,
    avgResponseTime,
    connectionDetails,
  } = useConnectionMonitor();

  const [
    editSmartNode,
    {
      isSuccess: isEditSmartNodeSuccess,
      isError: isEditSmartNodeError,
      error: editSmartNodeError,
      isLoading: isEditSmartNodeLoading,
    },
  ] = useEditSmartNodeMutation();

  const [qrCodeData, setQRCodeData] = React.useState(null);

  const scannedRef = React.useRef(false);
  const sentRef = React.useRef(false);
  const badQrToastRef = React.useRef(false);

  const refusedScan = useRefusedScan();

  // Only called when a scan is refused.
  const resetScan = () => {
    refusedScan.refuse(qrCodeData?.serial_number);
    scannedRef.current = false;
    sentRef.current = false;
    setQRCodeData(null);
  };

  let { job_card_reference_id } = useParams();
  const dispatch = useDispatch();
  let navigate = useNavigate();

  useEffect(() => { dispatch(setCurrentStep(1)); }, []);

  const { data: smartnode, isSuccess: isGetSmartnodeSuccess, isError: isGetSmartnodeError, error: getSmartNodeError } = useGetIMSSmartnodeQuery(
    qrCodeData ? { serial_number: qrCodeData.serial_number, imsi_number: qrCodeData.imsi_number } : skipToken
  );

  useEffect(() => {
    if (isGetSmartnodeError) {
      dispatch(showError(getSmartNodeError.data.error));
      resetScan();
    }
  }, [isGetSmartnodeError, smartnode]);

  useEffect(() => {
    if (isGetSmartnodeSuccess && qrCodeData) {
      if (!sentRef.current) {
        sentRef.current = true;
        const { serial_number, imsi_number } = qrCodeData;
        editSmartNode({
          body: { state: "BEFWA", serial_number, reference_id: job_card_reference_id, imsi_number, status: "In Field" },
        });
      }
    }
  }, [editSmartNode, job_card_reference_id, isGetSmartnodeSuccess, qrCodeData]);

  useEffect(() => {
    if (isEditSmartNodeSuccess) {
      navigate(`/installer/${job_card_reference_id}/${qrCodeData.serial_number}/${qrCodeData.imsi_number}/work_area_before_install`);
    }
  }, [isEditSmartNodeSuccess, navigate, job_card_reference_id, qrCodeData]);

  useEffect(() => {
    if (isEditSmartNodeError) {
      dispatch(showError(editSmartNodeError.data.error_message));
      resetScan();
    }
  }, [isEditSmartNodeError, editSmartNodeError, dispatch]);

  return (
    <>
      {showAlert && (
        <ConnectionAlert onRetry={handleRetry} onClose={handleDismiss} connectionDetails={connectionDetails} position="top" alertType="error" />
      )}
      {showWeakWarning && (
        <ConnectionAlert onRetry={handleRetry} onClose={handleWeakWarningDismiss} connectionDetails={connectionDetails} position="top" alertType="warning" responseTime={responseTime || avgResponseTime} />
      )}

      <div style={{ position: "relative" }}>
        <LoadingOverlay isLoading={isEditSmartNodeLoading} message="Processing QR Code..." />
        <NavbarStepper />
      </div>

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "calc(100vh - 100px)", backgroundColor: "#000", px: 2, pb: "90px" }}>
        {/* Viewfinder */}
        <Box sx={{ position: "relative", width: "85vw", height: "85vw", mb: 3 }}>
          {/* Corner brackets */}
          <Box sx={{ position: "absolute", top: 0, left: 0, width: 32, height: 32, borderTop: "3px solid #FDB940", borderLeft: "3px solid #FDB940", borderTopLeftRadius: "4px", zIndex: 2, pointerEvents: "none" }} />
          <Box sx={{ position: "absolute", top: 0, right: 0, width: 32, height: 32, borderTop: "3px solid #FDB940", borderRight: "3px solid #FDB940", borderTopRightRadius: "4px", zIndex: 2, pointerEvents: "none" }} />
          <Box sx={{ position: "absolute", bottom: 0, left: 0, width: 32, height: 32, borderBottom: "3px solid #FDB940", borderLeft: "3px solid #FDB940", borderBottomLeftRadius: "4px", zIndex: 2, pointerEvents: "none" }} />
          <Box sx={{ position: "absolute", bottom: 0, right: 0, width: 32, height: 32, borderBottom: "3px solid #FDB940", borderRight: "3px solid #FDB940", borderBottomRightRadius: "4px", zIndex: 2, pointerEvents: "none" }} />

          {/* Camera feed */}
          <Box sx={{ width: "100%", height: "100%", overflow: "hidden", "& section": { height: "100% !important", paddingTop: "0 !important" }, "& section > div": { paddingTop: "0 !important", height: "100% !important", position: "relative !important" }, "& video": { objectFit: "cover !important", position: "static !important", width: "100% !important", height: "100% !important" } }}>
            {!qrCodeData && (isConnected ? (
              <QrReader
                constraints={{ facingMode: "environment" }}
                onResult={(result) => {
                  if (!result || scannedRef.current) return;
                  const parsed = parseDeviceQr(result.text);
                  if (!parsed) {
                    if (!badQrToastRef.current) {
                      badQrToastRef.current = true;
                      dispatch(showError("Not a Tjommie QR code"));
                      setTimeout(() => { badQrToastRef.current = false; }, 3000);
                    }
                    return;
                  }
                  if (refusedScan.ignore(parsed.serial_number)) return;
                  scannedRef.current = true;
                  setQRCodeData(parsed);
                }}
              />
            ) : (
              <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", backgroundColor: "#111", color: "rgba(255,255,255,0.4)" }}>
                <Typography variant="body2">Camera disabled — no connection</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography sx={{ color: "rgba(255,255,255,0.8)", textAlign: "center", fontSize: "0.95rem" }}>
          Scan QR Code of the device being installed.
        </Typography>
      </Box>

      {/* Fixed bottom bar */}
      <Box sx={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        display: "flex", gap: "12px",
        px: "16px", py: "14px",
        background: "rgba(13,14,20,0.95)",
        backdropFilter: "blur(12px)",
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}>
        <AbortJobButton />
      </Box>
    </>
  );
}

export default React.memo(QRCodeScanPage);
